import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { StatementUploadStatuses } from './useStatementUploadStatuses.interface';
import fetchStatementUploadStatuses from './useStatementUploadStatuses.request';

import { useMember } from 'modules/common/queries/member';

export * from './useStatementUploadStatuses.interface';

export const STATEMENT_UPLOAD_STATUSES_QUERY_KEY = ['bookkeeping', 'useStatementUploadStatuses'];

/** Long enough to absorb a second observer mounting, short enough to never mask a poll. */
const STALE_TIME_MS = 5000;

/**
 * Per-document statement verdicts for the signed-in member.
 *
 * Member-scoped and stage-free: unlike the onboarding progress blob this was previously served
 * on, it answers for a member at any point in their lifecycle, which is what lets the dashboard
 * read it at all.
 *
 * A short `staleTime` rather than none. Interval refetches ignore `staleTime` entirely, so it costs
 * the polling caller nothing — what it suppresses is the redundant refetch when a second observer
 * mounts (the modal opening over the panel) moments after the first answer landed.
 */
export default function useStatementUploadStatuses(
    options?: Omit<UseQueryOptions<StatementUploadStatuses, AxiosError>, 'queryKey' | 'queryFn'>
) {
    const { data: member } = useMember();
    const memberId = member?.id;
    const { enabled: isEnabledByCaller = true, ...restOptions } = options ?? {};

    return useQuery<StatementUploadStatuses, AxiosError>({
        queryKey: [...STATEMENT_UPLOAD_STATUSES_QUERY_KEY, memberId ?? null],
        queryFn: () => fetchStatementUploadStatuses(memberId as number),
        staleTime: STALE_TIME_MS,
        refetchOnWindowFocus: false,
        retry: false,
        ...restOptions,
        // The endpoint requires member_id; firing before useMember resolves would 400.
        enabled: isEnabledByCaller && memberId != null,
    });
}
